import React from 'react';
import { z } from 'zod';
import { styled } from 'styled-components';
import type { BlockDefinition } from '../types';

// A simple stat strip — a cream rounded band with 2–4 big numbers + labels,
// divided by vertical rules (matches the live pricing page's
// "350+ / 3M+ / 5$" band). Content-driven; no interactivity.
export const statStripSchema = z.object({
  stats: z
    .array(z.object({ value: z.string().min(1), label: z.string().min(1) }))
    .min(2)
    .max(4),
});

type StatStripProps = z.infer<typeof statStripSchema>;

const Band = styled.section`
  max-width: 112rem;
  margin: 2rem auto 0;
  padding: 0 1.6rem;
`;

const Inner = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2.4rem;
  padding: 3.2rem 2.4rem;
  border-radius: 2rem;
  background: #faf9f5;

  @media (min-width: 48em) {
    grid-auto-flow: column;
    grid-auto-columns: 1fr;
    gap: 0;
  }
`;

const Stat = styled.div`
  text-align: center;
  padding: 0.4rem 2rem;

  @media (min-width: 48em) {
    & + & {
      border-left: 1px solid #e6e2d6;
    }
  }
`;

const Value = styled.div`
  color: #0b1020;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 700;
  font-size: clamp(3.6rem, 5vw, 5.6rem);
  line-height: 1.05;
`;

const Label = styled.div`
  margin-top: 0.8rem;
  color: #4e4f52;
  font-family: 'DM Sans', sans-serif;
  font-size: 1.5rem;
  line-height: 1.4;
`;

function StatStrip({ stats }: StatStripProps) {
  return (
    <Band>
      <Inner>
        {stats.map((s, i) => (
          <Stat key={i}>
            <Value>{s.value}</Value>
            <Label>{s.label}</Label>
          </Stat>
        ))}
      </Inner>
    </Band>
  );
}

export const statStripBlock: BlockDefinition<StatStripProps> = {
  type: 'statStrip',
  schema: statStripSchema,
  Renderer: StatStrip,
};
